export function getScaledCoords(e, canvas, scaleFactor) {
  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  return [x / scaleFactor, y / scaleFactor];
}

export function normalizePoints(points, imgWidth, imgHeight) {
  return points.map((point) => [
    Math.round((point[0] / imgWidth) * 100) / 100,
    Math.round((point[1] / imgHeight) * 100) / 100,
  ]);
}

export function formatPointsAsPython(
  pointArrays,
  showNormalized = false,
  imgWidth = 1,
  imgHeight = 1,
) {
  if (!pointArrays.length) return "";

  let processedPoints = pointArrays;

  if (showNormalized) {
    processedPoints = pointArrays.map((points) =>
      normalizePoints(points, imgWidth, imgHeight),
    );
  }

  // Clean empty points
  processedPoints = processedPoints.filter((points) => points.length > 0);

  if (!processedPoints.length) return "";

  const codeTemplate = `[\n${processedPoints
    .map((points) => {
      return `    np.array([${points
        .map((point) => {
          return `[${point[0]}, ${point[1]}]`;
        })
        .join(", ")}])`;
    })
    .join(",\n")}\n]`;

  return codeTemplate;
}

export function formatPointsAsJSON(
  pointArrays,
  showNormalized = false,
  imgWidth = 1,
  imgHeight = 1,
) {
  if (!pointArrays.length) return "";

  let processedPoints = pointArrays;

  if (showNormalized) {
    processedPoints = pointArrays.map((points) =>
      normalizePoints(points, imgWidth, imgHeight),
    );
  }

  // Clean empty points
  processedPoints = processedPoints.filter((points) => points.length > 0);

  if (!processedPoints.length) return "";

  const jsonTemplate = `[\n${processedPoints
    .map((points) => {
      return `    [${points
        .map((point) => {
          return `{"x": ${point[0]}, "y": ${point[1]}}`;
        })
        .join(", ")}]`;
    })
    .join(",\n")}\n]`;

  return jsonTemplate;
}

export function copyToClipboard(text) {
  // Strip whitespace on left and right
  const cleanText = text.replace(/^\s+|\s+$/g, "");
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(cleanText);
  }

  // Fallback for older browsers
  const textArea = document.createElement("textarea");
  textArea.value = cleanText;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
    textArea.remove();
    return Promise.resolve();
  } catch (err) {
    textArea.remove();
    return Promise.reject(err);
  }
}

export function downloadCanvasAsImage(canvas, filename = "polygon-image.png") {
  try {
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL();
    link.click();
  } catch (error) {
    console.error("Failed to export canvas:", error);
    // Fallback: create a blob and download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        alert("Failed to save image. Please try uploading your image again.");
      }
    });
  }
}

export function validateImageFile(file) {
  const supportedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  return supportedTypes.includes(file.type);
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!validateImageFile(file)) {
      reject(new Error("Only PNG, JPEG, JPG, and WebP files are allowed."));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// DeepStream Analytics Configuration Export Functions
export function formatDeepStreamCoordinates(
  pointArrays,
  zoneTypes,
  imgWidth = 1920,
  imgHeight = 1080,
) {
  if (!pointArrays.length)
    return "# No zones configured yet\n# Draw zones on the image to see coordinates here";

  let coordinateDisplay = `# DeepStream Analytics Zones (${pointArrays.length} zone${pointArrays.length !== 1 ? "s" : ""})\n`;
  coordinateDisplay += `# Image dimensions: ${imgWidth} x ${imgHeight}\n\n`;

  pointArrays.forEach((points, index) => {
    const zoneType = zoneTypes[index] || "roi_filtering";
    const coordString = points
      .map((p) => `${Math.round(p[0])};${Math.round(p[1])}`)
      .join(";");

    coordinateDisplay += `## Zone ${index + 1}: ${getZoneTypeLabel(zoneType)}\n`;

    switch (zoneType) {
      case "roi_filtering":
        coordinateDisplay += `roi-RF=${coordString}\n`;
        coordinateDisplay += `# ROI Filtering - ${points.length} points\n\n`;
        break;

      case "overcrowding":
        coordinateDisplay += `roi-OC=${coordString}\n`;
        coordinateDisplay += `# Overcrowding Detection - ${points.length} points\n\n`;
        break;

      case "line_crossing":
        if (points.length === 8) {
          // Line crossing format: x1d;y1d;x2d;y2d;x1c;y1c;x2c;y2c
          // First 4 values are direction line, last 4 are crossing line
          const directionCoords = `${Math.round(points[0])};${Math.round(points[1])};${Math.round(points[2])};${Math.round(points[3])}`;
          const crossingCoords = `${Math.round(points[4])};${Math.round(points[5])};${Math.round(points[6])};${Math.round(points[7])}`;
          coordinateDisplay += `line-crossing-Entry=${directionCoords};${crossingCoords}\n`;
          coordinateDisplay += `# Line Crossing - Direction: (${Math.round(points[0])},${Math.round(points[1])}) to (${Math.round(points[2])},${Math.round(points[3])}), Crossing: (${Math.round(points[4])},${Math.round(points[5])}) to (${Math.round(points[6])},${Math.round(points[7])})\n\n`;
        } else {
          coordinateDisplay += `# Line Crossing - Incomplete (need direction line + crossing line)\n\n`;
        }
        break;

      case "direction_detection":
        if (points.length >= 2) {
          const startPoint = points[0];
          const endPoint = points[points.length - 1];
          const directionCoords = `${Math.round(startPoint[0])};${Math.round(startPoint[1])};${Math.round(endPoint[0])};${Math.round(endPoint[1])}`;
          coordinateDisplay += `direction-Main=${directionCoords}\n`;
          coordinateDisplay += `# Direction Detection - Vector from (${Math.round(startPoint[0])},${Math.round(startPoint[1])}) to (${Math.round(endPoint[0])},${Math.round(endPoint[1])})\n\n`;
        } else {
          coordinateDisplay += `# Direction Detection - Incomplete (need 2 points)\n\n`;
        }
        break;
    }
  });

  return coordinateDisplay;
}

function getZoneTypeLabel(zoneType) {
  const labels = {
    roi_filtering: "ROI Filtering",
    overcrowding: "Overcrowding Detection",
    line_crossing: "Line Crossing",
    direction_detection: "Direction Detection",
  };
  return labels[zoneType] || "Unknown Zone";
}

export function formatPointsAsDeepStreamConfig(
  pointArrays,
  zoneTypes,
  imgWidth = 1920,
  imgHeight = 1080,
) {
  if (!pointArrays.length) return "";

  let config = `################################################################################
# DeepStream Analytics Configuration
# Generated by DeepStream Zone Tool - Pixuate
################################################################################

[property]
enable=1
# Width and height for configuration
config-width=${imgWidth}
config-height=${imgHeight}
# Display mode: 0=none, 1=lines/rois only, 2=all info
osd-mode=2
# OSD font size
display-font-size=12

`;

  // Group zones by type
  const zonesByType = {};
  pointArrays.forEach((points, index) => {
    const zoneType = zoneTypes[index] || "roi_filtering";
    if (!zonesByType[zoneType]) {
      zonesByType[zoneType] = [];
    }
    zonesByType[zoneType].push({ points, index });
  });

  // Generate configuration for each zone type
  Object.entries(zonesByType).forEach(([zoneType, zones]) => {
    zones.forEach((zone, zoneIndex) => {
      const { points, index } = zone;
      const coordString = points
        .map((p) => `${Math.round(p[0])};${Math.round(p[1])}`)
        .join(";");

      switch (zoneType) {
        case "roi_filtering":
          config += `## ROI Filtering Zone ${zoneIndex}
[roi-filtering-stream-${index}]
enable=1
# ROI coordinates: ${coordString}
roi-RF=${coordString}
# Remove objects in ROI (0=keep objects in ROI, 1=remove objects in ROI)
inverse-roi=0
# Class IDs to apply filtering (-1=all classes)
class-id=-1

`;
          break;

        case "overcrowding":
          config += `## Overcrowding Detection Zone ${zoneIndex}
[overcrowding-stream-${index}]
enable=1
# Overcrowding area coordinates: ${coordString}
roi-OC=${coordString}
# Object threshold for overcrowding detection
object-threshold=5
# Class IDs to monitor (-1=all classes)
class-id=-1

`;
          break;

        case "line_crossing":
          if (points.length === 8) {
            // Line crossing format: x1d;y1d;x2d;y2d;x1c;y1c;x2c;y2c
            const directionCoords = `${Math.round(points[0])};${Math.round(points[1])};${Math.round(points[2])};${Math.round(points[3])}`;
            const crossingCoords = `${Math.round(points[4])};${Math.round(points[5])};${Math.round(points[6])};${Math.round(points[7])}`;
            config += `## Line Crossing Zone ${zoneIndex}
[line-crossing-stream-${index}]
enable=1
# Line crossing configuration: direction_line;crossing_line
# Direction: (${Math.round(points[0])},${Math.round(points[1])}) to (${Math.round(points[2])},${Math.round(points[3])})
# Crossing: (${Math.round(points[4])},${Math.round(points[5])}) to (${Math.round(points[6])},${Math.round(points[7])})
line-crossing-Entry=${directionCoords};${crossingCoords}
# Class IDs to monitor
class-id=0
# Extended line crossing (0=exact line, 1=extended line)
extended=0
# Detection mode (loose, balanced, strict)
mode=balanced

`;
          }
          break;

        case "direction_detection":
          if (points.length >= 2) {
            const startPoint = points[0];
            const endPoint = points[points.length - 1];
            const directionCoords = `${Math.round(startPoint[0])};${Math.round(startPoint[1])};${Math.round(endPoint[0])};${Math.round(endPoint[1])}`;
            config += `## Direction Detection Zone ${zoneIndex}
[direction-detection-stream-${index}]
enable=1
# Direction vector coordinates
direction-Main=${directionCoords}
# Class IDs to monitor
class-id=0

`;
          }
          break;
      }
    });
  });

  return config;
}

export function exportDeepStreamConfig(
  pointArrays,
  zoneTypes,
  imgWidth,
  imgHeight,
  cameraName = "Camera",
  cameraId = "0",
  filename = "config_nvdsanalytics.txt",
) {
  let config = `# DeepStream Analytics Configuration
# Generated from PolygonZone Zone Editor
# Camera: ${cameraName} (ID: ${cameraId})

[property]
enable=1
config-width=${imgWidth}
config-height=${imgHeight}

`;

  // Group zones by type
  const lineCrossingZones = [];
  const roiFilteringZones = [];
  const overcrowdingZones = [];
  const directionZones = [];

  pointArrays.forEach((points, index) => {
    const zoneType = zoneTypes[index] || "roi_filtering";

    switch (zoneType) {
      case "line_crossing":
        lineCrossingZones.push({ points, index });
        break;
      case "roi_filtering":
        roiFilteringZones.push({ points, index });
        break;
      case "overcrowding":
        overcrowdingZones.push({ points, index });
        break;
      case "direction_detection":
        directionZones.push({ points, index });
        break;
    }
  });

  // Export line crossing zones in single block
  if (lineCrossingZones.length > 0) {
    config += `[line-crossing-stream-${cameraId}]\n`;
    config += `enable=1\n`;
    config += `class-id=-1\n`;
    config += `extended=1\n`;
    config += `mode=balanced\n`;

    lineCrossingZones.forEach((zone, zoneIndex) => {
      const { points } = zone;
      if (points.length === 8) {
        const coordString = `${Math.round(points[0])};${Math.round(points[1])};${Math.round(points[2])};${Math.round(points[3])};${Math.round(points[4])};${Math.round(points[5])};${Math.round(points[6])};${Math.round(points[7])}`;
        config += `line-crossing-tripwire-${zoneIndex + 1}-${cameraName}=${coordString}\n`;
      }
    });
    config += `\n`;
  }

  // Export ROI filtering zones in single block
  if (roiFilteringZones.length > 0) {
    config += `[roi-filtering-stream-${cameraId}]\n`;
    config += `enable=1\n`;
    config += `inverse-roi=0\n`;
    config += `class-id=-1\n`;

    roiFilteringZones.forEach((zone, zoneIndex) => {
      const { points } = zone;
      const coordString = points
        .map((p) => `${Math.round(p[0])};${Math.round(p[1])}`)
        .join(";");
      config += `roi-RF-zone-${zoneIndex + 1}-${cameraName}=${coordString}\n`;
    });
    config += `\n`;
  }

  // Export overcrowding zones in single block
  if (overcrowdingZones.length > 0) {
    config += `[overcrowding-stream-${cameraId}]\n`;
    config += `enable=1\n`;
    config += `object-threshold=5\n`;
    config += `class-id=-1\n`;

    overcrowdingZones.forEach((zone, zoneIndex) => {
      const { points } = zone;
      const coordString = points
        .map((p) => `${Math.round(p[0])};${Math.round(p[1])}`)
        .join(";");
      config += `roi-OC-zone-${zoneIndex + 1}-${cameraName}=${coordString}\n`;
    });
    config += `\n`;
  }

  // Export direction detection zones in single block
  if (directionZones.length > 0) {
    config += `[direction-detection-stream-${cameraId}]\n`;
    config += `enable=1\n`;
    config += `class-id=0\n`;

    directionZones.forEach((zone, zoneIndex) => {
      const { points } = zone;
      if (points.length >= 2) {
        const startPoint = points[0];
        const endPoint = points[points.length - 1];
        const directionCoords = `${Math.round(startPoint[0])};${Math.round(startPoint[1])};${Math.round(endPoint[0])};${Math.round(endPoint[1])}`;
        config += `direction-zone-${zoneIndex + 1}-${cameraName}=${directionCoords}\n`;
      }
    });
    config += `\n`;
  }

  const blob = new Blob([config], { type: "text/plain" });
  const link = document.createElement("a");
  link.download = filename;
  link.href = URL.createObjectURL(blob);
  link.click();
  URL.revokeObjectURL(link.href);
}

export function formatPointsAsYOLO(
  pointArrays,
  showNormalized = true,
  imgWidth = 1,
  imgHeight = 1,
) {
  if (!pointArrays.length) return "";

  let processedPoints = pointArrays;

  if (showNormalized) {
    processedPoints = pointArrays.map((points) =>
      normalizePoints(points, imgWidth, imgHeight),
    );
  }

  // Clean empty points
  processedPoints = processedPoints.filter((points) => points.length > 0);

  if (!processedPoints.length) return "";

  // YOLO format: class_id x1 y1 x2 y2 ... (normalized coordinates)
  return processedPoints
    .map((points, index) => {
      const coordString = points
        .map((point) => `${point[0]} ${point[1]}`)
        .join(" ");
      return `0 ${coordString}`; // Using class 0 for all zones
    })
    .join("\n");
}

// Import DeepStream Configuration
export function parseDeepStreamConfig(configText) {
  const zones = [];
  const lines = configText.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip comments and empty lines
    if (!line || line.startsWith('#') || line.startsWith('[')) {
      continue;
    }

    // Look for line-crossing entries
    if (line.includes('line-crossing-')) {
      const match = line.match(/line-crossing-[^=]+=\s*(.+)/);
      if (match) {
        const coords = match[1].trim().split(';').map(c => parseInt(c.trim()));

        // Line crossing format: x1d;y1d;x2d;y2d;x1c;y1c;x2c;y2c
        if (coords.length === 8) {
          zones.push({
            type: 'line_crossing',
            points: coords,
            name: line.split('=')[0].trim()
          });
        }
      }
    }
    // Look for ROI filtering entries
    else if (line.includes('roi-RF=')) {
      const match = line.match(/roi-RF=\s*(.+)/);
      if (match) {
        const coords = match[1].trim().split(';').map(c => parseInt(c.trim()));

        // Convert flat array to point pairs
        const points = [];
        for (let j = 0; j < coords.length; j += 2) {
          if (j + 1 < coords.length) {
            points.push([coords[j], coords[j + 1]]);
          }
        }

        if (points.length >= 3) {
          zones.push({
            type: 'roi_filtering',
            points: points,
            name: 'ROI Filtering Zone'
          });
        }
      }
    }
    // Look for overcrowding entries
    else if (line.includes('roi-OC=')) {
      const match = line.match(/roi-OC=\s*(.+)/);
      if (match) {
        const coords = match[1].trim().split(';').map(c => parseInt(c.trim()));

        // Convert flat array to point pairs
        const points = [];
        for (let j = 0; j < coords.length; j += 2) {
          if (j + 1 < coords.length) {
            points.push([coords[j], coords[j + 1]]);
          }
        }

        if (points.length >= 3) {
          zones.push({
            type: 'overcrowding',
            points: points,
            name: 'Overcrowding Zone'
          });
        }
      }
    }
    // Look for direction detection entries
    else if (line.includes('direction-')) {
      const match = line.match(/direction-[^=]+=\s*(.+)/);
      if (match) {
        const coords = match[1].trim().split(';').map(c => parseInt(c.trim()));

        // Direction format: x1;y1;x2;y2
        if (coords.length === 4) {
          zones.push({
            type: 'direction_detection',
            points: [[coords[0], coords[1]], [coords[2], coords[3]]],
            name: 'Direction Detection'
          });
        }
      }
    }
  }

  return zones;
}

export function loadConfigFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const configText = event.target.result;
        const zones = parseDeepStreamConfig(configText);
        resolve(zones);
      } catch (error) {
        reject(new Error('Failed to parse config file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
